import React, { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router';
import { HotelOption } from '../components/HotelRedirectHandler';
import { Loading } from '../components/Loading';
import { usePluginspace } from '../hooks/usePluginspace';
import api from '../services/api';

type HotelHandlerContextType = {
    hotelReservationContext: any;
};




const HotelHandlerContext = createContext<HotelHandlerContextType>({} as HotelHandlerContextType);

type HotelHandlerContextProviderProps = {
    children: ReactNode;
};

const HotelHandlerProvider: FC<HotelHandlerContextProviderProps> = (props) => {
    const { children } = props;
    const hotelReservationContext = {}; // Initialize your context value here
    const navigate = useNavigate();
    const { getToken, pluginspace } = usePluginspace();
    const [loading, setLoading] = useState(true);
    const hasProcessed = useRef(false);
    const isInitialNavigation = useRef(true);
    const [hotelOptions, setHotelOptions] = useState<HotelOption[]>([]);
    const [showModal, setShowModal] = useState(false);
    const location = useLocation();
    const navigationType = useNavigationType();


    useEffect(() => {
        const run = async () => {
            // Evita reprocessamento se já foi executado
            if (hasProcessed.current) return;

            // Verificar se já existe contexto salvo
            const existingContext = sessionStorage.getItem("hotelReservationContext");
            if (existingContext) {
                hasProcessed.current = true;
                return;
            }

            // Verificar se pluginspace está disponível
            if (!pluginspace?.id) {
                return; // Aguardar pluginspace carregar
            }

            // IMPORTANTE: Setar flag IMEDIATAMENTE antes de qualquer operação assíncrona
            hasProcessed.current = true;

            const params = new URLSearchParams(window.location.search);

            // Only accept explicit reservation + father (pluginspace) and guest token
            const reservation =
                params.get("reservation") || params.get("reservationId") || null;
            const father =
                params.get("father") ||
                params.get("pluginspace") ||
                params.get("pluginspaceId") ||
                null;
            const integration = params.get("integration") || null;
            const token = await getToken();

            if (father && !reservation && token && !integration) {
                try {
                    const pluginspaceResp = await api.post(
                        `/api/store-relations/parents`,
                        {
                            child: pluginspace.id,
                            hasLink: true,
                            father: father ? parseInt(father) : undefined,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (pluginspaceResp?.data?.data) {
                        const hotels: HotelOption[] = pluginspaceResp.data.data.map(
                            (item: any) => ({
                                pluginspaceId: item.parentPluginspace.id,
                                name: item.parentPluginspace.name,
                                subdomain: item.parentPluginspace.subdomain,
                                image: item.storesHotel?.img || "",
                                description: item.parentPluginspace.type || "",
                                paymentIntegrated: item.storesHotel?.paymentIntegrated || false,
                            })
                        );

                        // Se vier apenas 1 hotel, selecionar automaticamente
                        if (hotels.length === 1) {
                            const selectedHotel = hotels[0];
                            const viewOnlyContext = {
                                pluginspaceId: selectedHotel.pluginspaceId,
                                viewOnly: !selectedHotel.paymentIntegrated,
                                subdomain: selectedHotel.subdomain,
                                hotelSelectedByUser: true,
                            };

                            sessionStorage.setItem(
                                "hotelReservationContext",
                                JSON.stringify(viewOnlyContext)
                            );
                        } else if (hotels.length > 1) {
                            setHotelOptions(hotels);
                            setShowModal(true);
                        }
                    }
                } catch (e) {
                    console.warn("Could not fetch parent hotels with father param", e);
                }
                return;
            }

            // Processar quando vier reservation + father
            if (!reservation || !father) {
                return;
            }

            try {
                // Call server POST find-by-id with body { id, pluginspaceId }
                const resp = await api.post(
                    "/api/hotel-reservation/find-by-id",
                    { reservationId: reservation, pluginspaceId: father },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (resp && resp.data) {
                    // pick only minimal fields
                    const raw = resp.data.data ? resp.data.data : resp.data;
                    const candidate = Array.isArray(raw) ? raw[0] : raw;
                    const pick = (ctx: any) => {
                        if (!ctx || typeof ctx !== "object") return undefined;
                        const out: any = {};

                        // reservation id
                        if (ctx.id) out.reservationId = ctx.id;

                        // customer document: prefer customer.doc, fallback to cpf-like fields
                        const customerDoc =
                            ctx.customer?.doc ||
                            ctx.customer?.cpf ||
                            ctx.customer_doc ||
                            ctx.customerDoc ||
                            ctx.cpf;
                        if (customerDoc) out.doc = customerDoc;

                        if (father) out.pluginspaceId = father;

                        const subdomain = ctx.pluginspace.subdomain;
                        if (subdomain) out.subdomain = subdomain;

                        // room id: prefer nested room.id, fallback to top-level roomId/room_id
                        const roomId = ctx.room?.id ?? ctx.roomId ?? ctx.room_id;
                        if (roomId) out.roomId = roomId;

                        if (integration === "false" || !integration) {
                            out.viewOnly = true;
                        }

                        return Object.keys(out).length > 0 ? out : undefined;
                    };

                    const minimal = pick(candidate.dados[0]);
                    if (minimal) {
                        // Save to sessionStorage (frontend fallback)
                        try {
                            sessionStorage.setItem(
                                "hotelReservationContext",
                                JSON.stringify(minimal)
                            );
                        } catch (e) {
                            console.warn(
                                "Could not save hotelReservationContext to sessionStorage",
                                e
                            );
                        }

                        // server-side session endpoint not present; only persist to sessionStorage
                    }
                }
            } catch (err: unknown) {
                const msg =
                    err && typeof err === "object" && "message" in err
                        ? (err as any).message
                        : String(err);
                console.warn("hotel context save failed", msg);
            }

            // remove only the token/reservation/father params to avoid leaking them
            params.delete("guest_token");
            params.delete("token_id");
            params.delete("token");
            params.delete("reservation");
            params.delete("reservationId");
            params.delete("father");
            params.delete("pluginspace");
            params.delete("pluginspaceId");
            params.delete("integration");

            const newSearch = params.toString();
            // Do NOT modify the path; keep any path segment intact.
            const newUrl =
                window.location.pathname +
                (newSearch ? `?${newSearch}` : "") +
                window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        };

        run().catch((e) => {
            console.warn("HotelRedirectHandler error", e);
            // Reset flag em caso de erro para permitir retry
            hasProcessed.current = false;
        }).finally(() => {
            setTimeout(() => {
                setLoading(false);
            }, 1000); // Pequeno delay para evitar flicker caso o processamento seja muito rápido
        });
    }, [navigate, getToken, pluginspace?.id]);
    const initialLocationKey = useRef(location.key);

    useEffect(() => {
        // Só intercepta se o botão de voltar (POP) for pressionado
        if (navigationType !== 'POP') return;

        // Detecta o índice da página atual no histórico do React Router
        const historyState = window.history.state;
        const currentIdx = historyState?.idx ?? historyState?.usr?.idx;

        // Se o índice for maior que 0, o usuário está voltando para uma página INTERNA.
        // Portanto, interrompemos a função e deixamos o React Router funcionar normalmente.
        if (currentIdx !== undefined && currentIdx > 0) return;

        // --- Daqui para baixo é a sua lógica original de redirecionamento ---
        const stored = sessionStorage.getItem("hotelReservationContext");
        if (!stored) return;

        try {
            const ctx = JSON.parse(stored) as { subdomain?: string };
            if (!ctx.subdomain) return;

            const apiUrl = process.env.REACT_APP_API_URL ?? "";
            let baseUrl = "http://hospitality.ninegrid.com.br/";
            if (apiUrl.includes("staging")) baseUrl = "https://hospitality-staging.ninegrid.com.br/";
            else if (apiUrl.includes("localhost")) baseUrl = "http://localhost:7040/";

            const encodedSubdomain = window.btoa(unescape(encodeURIComponent(ctx.subdomain)));

            // Redireciona para o hotel parceiro
            window.location.href = `${baseUrl}${encodedSubdomain}`;
        } catch (e) {
            console.warn("Error handling back navigation to hotel", e);
        }
    }, [location, navigationType]);

    if (loading) {
        return (
            <div
                style={{
                    height: "100vh",
                }}
            >
                <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
            </div>
        );
    }

    return (
        <HotelHandlerContext.Provider value={{ hotelReservationContext }}>
            {children}
        </HotelHandlerContext.Provider>
    );
}

export default HotelHandlerProvider;

export const useHotelHandlerContext = () => {
    return useContext(HotelHandlerContext);
}
